<template>
  <div class="api-webhook-config">
    <div class="form-group">
      <div class="auto-execute-row">
        <label class="auto-execute-label">
          <input
            v-model="webhookConfig.auto_execute"
            type="checkbox"
            class="auto-execute-checkbox"
            @change="updateValue"
          />
          <span>{{ $t('MACROS.API_WEBHOOK.AUTO_EXECUTE_LABEL') }}</span>
        </label>
        <fluent-icon
          v-tooltip="$t('MACROS.API_WEBHOOK.AUTO_EXECUTE_HELP')"
          icon="info"
          size="16"
          class="auto-execute-info"
        />
      </div>
    </div>

    <div class="form-group">
      <label class="form-label">{{ $t('MACROS.API_WEBHOOK.URL_LABEL') }}</label>
      <input
        v-model="webhookConfig.url"
        type="url"
        class="form-input"
        :placeholder="$t('MACROS.API_WEBHOOK.URL_PLACEHOLDER')"
        @input="updateValue"
      />
    </div>

    <div class="form-group">
      <label class="form-label">{{ $t('MACROS.API_WEBHOOK.METHOD_LABEL') }}</label>
      <multiselect
        v-model="selectedMethod"
        track-by="id"
        label="name"
        :placeholder="$t('FORMS.MULTISELECT.SELECT')"
        :options="httpMethodOptions"
        :searchable="false"
        :allow-empty="false"
        :show-labels="false"
        :close-on-select="true"
        deselect-label=""
        select-label=""
        selected-label=""
        class="http-method-select"
        @select="onMethodSelect"
      />
    </div>

    <div class="form-group">
      <label class="form-label">{{ $t('MACROS.API_WEBHOOK.HEADERS_LABEL') }}</label>
      <textarea
        v-model="webhookConfig.headers"
        class="form-textarea"
        rows="3"
        :placeholder="$t('MACROS.API_WEBHOOK.HEADERS_PLACEHOLDER')"
        @input="updateValue"
      />
      <small class="form-help">{{ $t('MACROS.API_WEBHOOK.HEADERS_HELP') }}</small>
    </div>

    <div class="form-group">
      <div class="flex items-center justify-between mb-1">
        <label class="form-label">{{ $t('MACROS.API_WEBHOOK.PAYLOAD_LABEL') }}</label>
        <button
          v-if="hasDynamicVariables"
          type="button"
          class="insert-variable-btn"
          @click="showVariableDropdown = !showVariableDropdown"
        >
          {{ $t('MACROS.API_WEBHOOK.INSERT_VARIABLE') }}
        </button>
      </div>
      <div class="relative">
      <textarea
          ref="payloadTextarea"
          v-model="webhookConfig.payload"
        class="form-textarea"
          rows="8"
          :placeholder="$t('MACROS.API_WEBHOOK.PAYLOAD_PLACEHOLDER')"
          @input="onPayloadInput"
          @focus="checkForDynamicVariables"
        />
        <div
          v-if="showVariableDropdown"
          class="variable-dropdown"
        >
          <div
            v-for="variable in availableVariables"
            :key="variable.key"
            class="variable-item"
            @click="insertVariable(variable.key)"
          >
            <span class="variable-label">{{ variable.label }}</span>
            <span class="variable-key">({{ variable.key }})</span>
          </div>
        </div>
      </div>
      <small class="form-help">{{ $t('MACROS.API_WEBHOOK.PAYLOAD_HELP') }}</small>
      <div v-if="payloadVariables.length > 0" class="payload-variables-info">
        <p class="variables-label">{{ $t('MACROS.API_WEBHOOK.DETECTED_VARIABLES') }}:</p>
        <div class="variables-list">
          <span
            v-for="(variable, index) in payloadVariables"
            :key="index"
            class="variable-badge"
          >
            {{ variable }}
          </span>
        </div>
      </div>
    </div>

    <div class="form-group">
      <label class="form-label">{{ $t('MACROS.API_WEBHOOK.RESPONSE_FIELDS_LABEL') }}</label>
      <div class="response-fields-list">
        <div class="response-field-item">
          <span class="field-number">1.</span>
          <div class="field-wrapper">
            <label class="field-label">{{ $t('MACROS.API_WEBHOOK.FIELD_1_LABEL') }}</label>
            <input
              v-model="webhookConfig.response_fields[0]"
              type="text"
              class="form-input"
              :class="{ 'input-error': fieldErrors[0] }"
              :placeholder="$t('MACROS.API_WEBHOOK.FIELD_1_PLACEHOLDER_SHORT')"
              @input="validateAndUpdateField(0)"
              @blur="validateField(0)"
            />
            <span v-if="fieldErrors[0]" class="error-message">
              {{ fieldErrors[0] }}
            </span>
          </div>
        </div>
        <div class="response-field-item">
          <span class="field-number">2.</span>
          <div class="field-wrapper">
            <label class="field-label">{{ $t('MACROS.API_WEBHOOK.FIELD_2_LABEL') }}</label>
            <input
              v-model="webhookConfig.response_fields[1]"
              type="text"
              class="form-input"
              :class="{ 'input-error': fieldErrors[1] }"
              :placeholder="$t('MACROS.API_WEBHOOK.FIELD_2_PLACEHOLDER_SHORT')"
              @input="validateAndUpdateField(1)"
              @blur="validateField(1)"
            />
            <span v-if="fieldErrors[1]" class="error-message">
              {{ fieldErrors[1] }}
            </span>
          </div>
        </div>
      </div>
      <small class="form-help">{{ $t('MACROS.API_WEBHOOK.RESPONSE_FIELDS_HELP') }}</small>
      <small class="form-help form-help-nested">{{ $t('MACROS.API_WEBHOOK.NESTED_FIELDS_HELP') }}</small>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { buildMessageVariableOptions } from 'dashboard/helper/messageVariableOptions';
import Multiselect from 'vue-multiselect';

export default {
  components: {
    Multiselect,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      webhookConfig: {
        url: '',
        method: 'POST',
        headers: '',
        payload: '',
        response_fields: ['message_body', 'email_subject'],
        auto_execute: false,
      },
      httpMethodOptions: [
        { id: 'POST', name: 'POST' },
        { id: 'GET', name: 'GET' },
        { id: 'PUT', name: 'PUT' },
        { id: 'PATCH', name: 'PATCH' },
        { id: 'DELETE', name: 'DELETE' },
      ],
      showVariableDropdown: false,
      payloadVariables: [],
      fieldErrors: [null, null],
    };
  },
  computed: {
    ...mapGetters({
      contactCustomAttributes: 'attributes/getAttributesByModel',
    }),
    availableVariables() {
      return buildMessageVariableOptions(
        this.contactCustomAttributes('contact_attribute') || []
      );
    },
    selectedMethod: {
      get() {
        return this.httpMethodOptions.find(
          option => option.id === this.webhookConfig.method
        ) || this.httpMethodOptions[0];
      },
      set(value) {
        // Handled by onMethodSelect
      },
    },
    hasDynamicVariables() {
      return this.webhookConfig.payload && this.webhookConfig.payload.includes('{{');
    },
  },
  mounted() {
    if (this.value && Object.keys(this.value).length > 0) {
      this.webhookConfig = {
        url: this.value.url || '',
        method: this.value.method || 'POST',
        headers: this.value.headers || '',
        payload: this.value.payload || '',
        response_fields: this.value.response_fields || ['message_body', 'email_subject'],
        auto_execute: Boolean(this.value.auto_execute),
      };
    }
    if (this.webhookConfig.response_fields.length < 2) {
      this.webhookConfig.response_fields = ['message_body', 'email_subject'];
    }
    this.checkForDynamicVariables();
    // Push the normalized config up so `auto_execute` is always persisted, even for
    // macros created before this field existed.
    this.updateValue();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    onMethodSelect(selected) {
      if (selected) {
        this.webhookConfig.method = selected.id;
        this.updateValue();
      }
    },
    updateValue() {
      this.$emit('input', { ...this.webhookConfig });
    },
    onPayloadInput() {
      this.checkForDynamicVariables();
      this.updateValue();
    },
    checkForDynamicVariables() {
      if (!this.webhookConfig.payload) {
        this.payloadVariables = [];
        return;
      }
      // Extract all {{variable}} patterns
      const matches = this.webhookConfig.payload.match(/\{\{([^}]+)\}\}/g);
      if (matches) {
        this.payloadVariables = matches.map(match => 
          match.replace(/\{\{|\}\}/g, '').trim()
        );
      } else {
        this.payloadVariables = [];
      }
    },
    insertVariable(variableKey) {
      const textarea = this.$refs.payloadTextarea;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = this.webhookConfig.payload;
      const before = text.substring(0, start);
      const after = text.substring(end);

      // Insert the variable with {{ }} syntax
      const variableText = `{{${variableKey}}}`;
      this.webhookConfig.payload = before + variableText + after;

      // Set cursor position after inserted variable
      this.$nextTick(() => {
        const newPosition = start + variableText.length;
        textarea.setSelectionRange(newPosition, newPosition);
        textarea.focus();
        this.checkForDynamicVariables();
        this.updateValue();
      });

      this.showVariableDropdown = false;
    },
    handleClickOutside(event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.showVariableDropdown = false;
      }
    },
    validateField(index) {
      const field = this.webhookConfig.response_fields[index];
      
      // Allow empty field
      if (!field || field.trim() === '') {
        this.fieldErrors[index] = null;
        this.$forceUpdate();
        return true;
      }
      
      const trimmedField = field.trim();
      
      // Check for spaces (except in valid dot notation)
      if (trimmedField !== field) {
        this.fieldErrors[index] = this.$t('MACROS.API_WEBHOOK.ERROR_LEADING_TRAILING_SPACES');
        this.$forceUpdate();
        return false;
      }
      
      // Check for invalid characters
      // Valid: alphanumeric, underscore, dash, dot (for nested paths)
      const validPattern = /^[a-zA-Z_][a-zA-Z0-9_.-]*$/;
      if (!validPattern.test(trimmedField)) {
        this.fieldErrors[index] = this.$t('MACROS.API_WEBHOOK.ERROR_INVALID_CHARACTERS');
        this.$forceUpdate();
        return false;
      }
      
      // Check for consecutive dots
      if (trimmedField.includes('..')) {
        this.fieldErrors[index] = this.$t('MACROS.API_WEBHOOK.ERROR_CONSECUTIVE_DOTS');
        this.$forceUpdate();
        return false;
      }
      
      // Check if starts or ends with dot
      if (trimmedField.startsWith('.') || trimmedField.endsWith('.')) {
        this.fieldErrors[index] = this.$t('MACROS.API_WEBHOOK.ERROR_DOT_POSITION');
        this.$forceUpdate();
        return false;
      }
      
      // Check each segment between dots
      const segments = trimmedField.split('.');
      for (const segment of segments) {
        if (segment.trim() === '') {
          this.fieldErrors[index] = this.$t('MACROS.API_WEBHOOK.ERROR_EMPTY_SEGMENT');
          this.$forceUpdate();
          return false;
        }
        // Each segment should start with letter or underscore
        if (!/^[a-zA-Z_]/.test(segment)) {
          this.fieldErrors[index] = this.$t('MACROS.API_WEBHOOK.ERROR_SEGMENT_START');
          this.$forceUpdate();
          return false;
        }
        // Each segment should only contain letters, numbers, underscores, and dashes
        if (!/^[a-zA-Z_][a-zA-Z0-9_-]*$/.test(segment)) {
          this.fieldErrors[index] = this.$t('MACROS.API_WEBHOOK.ERROR_INVALID_CHARACTERS');
          this.$forceUpdate();
          return false;
        }
      }
      
      // Valid field
      this.fieldErrors[index] = null;
      this.$forceUpdate();
      return true;
    },
    validateAndUpdateField(index) {
      this.validateField(index);
      this.updateValue();
    },
  },
};
</script>

<style scoped lang="scss">
.api-webhook-config {
  @apply mt-2 space-y-3;
}

.auto-execute-row {
  @apply flex flex-row items-start gap-2;
}

.auto-execute-label {
  @apply flex flex-row items-start gap-2 cursor-pointer text-sm font-medium text-slate-700 dark:text-slate-300 leading-none;
}

.auto-execute-checkbox {
  @apply h-4 w-4 m-0 rounded border-slate-300 dark:border-slate-600 text-woot-600 focus:ring-woot-500 shrink-0;
}

.auto-execute-info {
  @apply text-slate-400 dark:text-slate-500 cursor-help mt-0.5 shrink-0;
}

.form-group {
  @apply flex flex-col;
}

.form-label {
  @apply text-sm font-medium text-slate-700 dark:text-slate-300 mb-1;
}

.form-input,
.form-textarea {
  @apply w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-md 
         bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100
         focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent;
  min-height: 40px;
}

.form-textarea {
  @apply font-mono text-xs;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.form-help {
  @apply text-xs text-slate-500 dark:text-slate-400 mt-1;
}

.insert-variable-btn {
  @apply px-3 py-1 text-xs font-medium text-woot-600 dark:text-woot-400 
         bg-woot-50 dark:bg-woot-900 border border-woot-200 dark:border-woot-700 
         rounded-md hover:bg-woot-100 dark:hover:bg-woot-800 
         transition-colors duration-200;
}

.variable-dropdown {
  @apply absolute z-50 mt-1 w-full max-h-60 overflow-auto 
         bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 
         rounded-md shadow-lg;
  top: 100%;
  left: 0;
}

.variable-item {
  @apply px-3 py-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 
         flex items-center justify-between border-b border-slate-100 dark:border-slate-700 
         last:border-b-0;
  
  .variable-label {
    @apply text-sm font-medium text-slate-700 dark:text-slate-200;
  }
  
  .variable-key {
    @apply text-xs text-slate-500 dark:text-slate-400 font-mono;
  }
}

.payload-variables-info {
  @apply mt-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-md;
  
  .variables-label {
    @apply text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1;
  }
  
  .variables-list {
    @apply flex flex-wrap gap-1;
  }
  
  .variable-badge {
    @apply inline-block px-2 py-1 text-xs bg-woot-100 dark:bg-woot-900 
           text-woot-700 dark:text-woot-300 rounded-md font-mono;
  }
}

.test-result {
  @apply ml-2 text-sm;

  &.success {
    @apply text-green-600 dark:text-green-400;
  }

  &.error {
    @apply text-red-600 dark:text-red-400;
  }
}

.add-field-btn {
  @apply px-3 py-1 text-xs font-medium text-woot-600 dark:text-woot-400 
         bg-woot-50 dark:bg-woot-900 border border-woot-200 dark:border-woot-700 
         rounded-md hover:bg-woot-100 dark:hover:bg-woot-800 
         transition-colors duration-200;
}

.response-fields-list {
  @apply space-y-3;
}

.response-field-item {
  @apply flex items-start gap-3;
  
  .field-number {
    @apply text-sm font-semibold text-slate-600 dark:text-slate-400 min-w-[24px] pt-7;
  }
  
  .field-wrapper {
    @apply flex-1 flex flex-col gap-1;
  }
  
  .field-label {
    @apply text-xs font-medium text-slate-600 dark:text-slate-400;
  }
}

.form-help-nested {
  @apply mt-2 p-2 border-l-2;
  background-color: rgba(59, 130, 246, 0.1);
  border-color: rgba(59, 130, 246, 0.5);
}

.dark .form-help-nested {
  background-color: rgba(59, 130, 246, 0.1);
  border-color: rgba(96, 165, 250, 0.5);
}

.input-error {
  @apply border-red-500 focus:ring-red-500;
}

.dark .input-error {
  @apply border-red-400;
}

.error-message {
  @apply text-xs mt-1 block;
  color: #dc2626;
}

.dark .error-message {
  color: #f87171;
}

// ============================================
// Clean & Elegant Multiselect Styling (HTTP Method)
// ============================================

// Base multiselect styling
:deep(.multiselect) {
  min-height: 40px;
  font-size: 14px;
  margin-bottom: 0 !important;
}

:deep(.multiselect__tags) {
  min-height: 40px !important;
  max-height: 40px !important;
  padding: 8px 40px 8px 12px !important;
  border: 1.5px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: #ffffff;
  display: flex;
  align-items: center;
}

// Ensure tags container stays consistent when dropdown is open
:deep(.multiselect--active .multiselect__tags) {
  min-height: 40px !important;
  max-height: 40px !important;
  padding: 8px 40px 8px 12px !important;
  border-color: #3b82f6;
}

:deep(.multiselect__input) {
  font-size: 14px;
  padding: 0;
  min-height: auto;
  line-height: 1.5;
  border: none !important;
  box-shadow: none !important;
  background-color: transparent !important;
}

:deep(.multiselect__input:focus) {
  outline: none !important;
}

:deep(.multiselect__single) {
  font-size: 14px;
  margin-bottom: 0;
  padding: 0;
  line-height: 1.5;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

:deep(.multiselect__placeholder) {
  font-size: 14px !important;
  color: #9ca3af;
  padding: 0 !important;
  margin-bottom: 0 !important;
  line-height: 1.5 !important;
  white-space: nowrap;
  overflow: hidden;
  display: block;
}

// Ensure placeholder doesn't change when dropdown is open
:deep(.multiselect--active .multiselect__placeholder) {
  font-size: 14px !important;
  padding: 0 !important;
  margin-bottom: 0 !important;
  line-height: 1.5 !important;
}

:deep(.multiselect__select) {
  height: 40px;
  width: 40px;
  padding: 4px 8px;
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.multiselect__select:before) {
  border-color: #6b7280 transparent transparent;
  border-width: 5px 5px 0;
  position: static;
  margin: 0;
}

:deep(.multiselect__content-wrapper) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  margin-top: 2px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  max-height: 240px;
  overflow-y: auto;
  z-index: 50;
  
  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
  
  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}

:deep(.multiselect__content) {
  width: 100%;
  display: inline-block;
}

:deep(.multiselect__option) {
  font-size: 14px;
  padding: 10px 12px;
  min-height: 40px;
  line-height: 20px;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  font-weight: 500;
  
  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    max-width: 100%;
  }
  
  &:not(.multiselect__option--selected)::after {
    content: none !important;
  }

  &.multiselect__option--selected {
    padding-right: 2.25rem !important;
  }
}

:deep(.multiselect__option--highlight) {
  background: #eff6ff;
  color: #1e40af;
  
  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

:deep(.multiselect__option--selected) {
  background: #dbeafe;
  color: #1e40af;
  font-weight: 600;
}

:deep(.multiselect__option--selected.multiselect__option--highlight) {
  background: #bfdbfe;
  color: #1e3a8a;
}

// Dark mode for multiselect
.dark {
  :deep(.multiselect__tags) {
    background: #1f2937;
    border-color: #374151;
  }
  
  :deep(.multiselect--active .multiselect__tags) {
    border-color: #3b82f6;
  }
  
  :deep(.multiselect__single) {
    color: #f3f4f6;
  }
  
  :deep(.multiselect__placeholder) {
    color: #6b7280;
  }
  
  :deep(.multiselect__content-wrapper) {
    background: #1f2937;
    border-color: #374151;
  }
  
  :deep(.multiselect__option) {
    color: #f3f4f6;
  }
  
  :deep(.multiselect__option--highlight) {
    background: #374151;
    color: #60a5fa;
  }
  
  :deep(.multiselect__option--selected) {
    background: #1e3a8a;
    color: #93c5fd;
  }
}
</style>
